import React from "react";
import { LayerType, Shape } from "../../../types";
import { useMapCanvasInteraction, useMapCanvasShapes } from "../contexts/MapCanvasContext";
import { DraggableShape } from "../shapes/DraggableShape/DraggableShape";
import { ShapeProvider } from "../shapes/ShapeContext/ShapeProvider";

const nothing = () => {};

interface ViewShapeItemProps {
  shape: Shape;
  isInSelection: boolean;
}

const ViewShapeItem = React.memo<ViewShapeItemProps>(
  ({ shape, isInSelection }) => (
    <ShapeProvider
      shape={shape}
      isSelected={false}
      isInSelection={isInSelection}
      onSelect={nothing}
      onUpdate={nothing}
      otherShapes={[]}
      disabled={false}
      isMultiSelected={false}
      onDelete={nothing}
      onLocate={nothing}
      onMoveUp={nothing}
      onMoveDown={nothing}
    >
      <DraggableShape />
    </ShapeProvider>
  )
);

ViewShapeItem.displayName = "ViewShapeItem";

export const useGetViewShapesByType = () => {
  const { shapes } = useMapCanvasShapes();
  const { shapesInSelection } = useMapCanvasInteraction();

  const shapesInSelectionSet = React.useMemo(
    () => new Set(shapesInSelection),
    [shapesInSelection]
  );

  // Pre-group shapes by layer type — avoids filtering all shapes 4 times per render
  const shapesByLayer = React.useMemo(() => {
    const map = new Map<LayerType, Shape[]>();
    for (const shape of shapes) {
      const list = map.get(shape.layerType);
      if (list) {
        list.push(shape);
      } else {
        map.set(shape.layerType, [shape]);
      }
    }
    return map;
  }, [shapes]);

  return (type: LayerType): React.ReactNode => {
    return (shapesByLayer.get(type) || []).map((shape) => (
      <ViewShapeItem
        key={shape.id}
        shape={shape}
        isInSelection={shapesInSelectionSet.has(shape.id)}
      />
    ));
  };
};
